import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';
import SpinnerOverlay from '../../components/ui/SpinnerOverlay';
import SuccessToast from '../../components/ui/SuccessToast';

const CorporateContractsScreen = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [leases, setLeases] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [activeTab, setActiveTab] = useState('requests');
  const [quoteModal, setQuoteModal] = useState({ show: false, requestId: null });
  const [quoteForm, setQuoteForm] = useState({ monthlyPrice: '', depositTerms: '', maintenanceFee: '', overMileageRate: '', slaTerms: '' });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [reqRes, leasesRes, invoicesRes] = await Promise.all([
        supabase?.from('corporate_lease_requests')?.select(`*, corporate_accounts(company_name, billing_email, contact_person)`)?.order('created_at', { ascending: false }),
        supabase?.from('corporate_leases')?.select(`*, corporate_accounts(company_name)`)?.order('created_at', { ascending: false }),
        supabase?.from('rental_invoices')?.select(`*, corporate_accounts(company_name)`)?.eq('reference_type', 'lease')?.order('created_at', { ascending: false }),
      ]);
      setRequests(reqRes?.data || []);
      setLeases(leasesRes?.data || []);
      setInvoices(invoicesRes?.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleSendQuote = async () => {
    if (!quoteModal?.requestId) return;
    setProcessing(quoteModal?.requestId);
    try {
      const { data, error } = await supabase?.rpc('create_corporate_lease_quote', {
        p_request_id: quoteModal?.requestId,
        p_admin_id: user?.id,
        p_quote_details: quoteForm,
      });
      if (error) throw error;
      if (data?.success) {
        setSuccessMsg('Quote sent successfully!');
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 2000);
        setQuoteModal({ show: false, requestId: null });
        fetchData();
      }
    } catch (e) { console.error(e); }
    finally { setProcessing(null); }
  };

  const handleCreateInvoice = async (leaseId, corpId, amount) => {
    setProcessing(leaseId);
    try {
      const invoiceNumber = 'INV-' + Date.now()?.toString()?.slice(-8);
      const dueDate = new Date();
      dueDate?.setDate(dueDate?.getDate() + 30);
      const { error } = await supabase?.from('rental_invoices')?.insert({
        invoice_number: invoiceNumber,
        reference_type: 'lease',
        reference_id: leaseId,
        corporate_id: corpId,
        amount: amount,
        inv_status: 'pending',
        due_date: dueDate?.toISOString()?.split('T')?.[0],
      });
      if (!error) {
        setSuccessMsg('Invoice created!');
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 2000);
        fetchData();
      }
    } catch (e) { console.error(e); }
    finally { setProcessing(null); }
  };

  const reqStatusColors = { submitted: '#60a5fa', quoted: '#fbbf24', negotiating: '#f97316', approved: '#34d399', rejected: '#f87171' };
  const leaseStatusColors = { draft: '#94a3b8', sent: '#60a5fa', accepted: '#34d399', active: '#ffd700', ended: '#a78bfa' };
  const invStatusColors = { pending: '#fbbf24', paid: '#34d399', overdue: '#f87171', cancelled: '#94a3b8' };
  const formatDate = (d) => d ? new Date(d)?.toLocaleDateString('en-KE', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

  const inputStyle = { background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)' };
  const inputClass = "w-full px-4 py-3 rounded-xl text-white placeholder-white/30 text-sm outline-none focus:ring-2 focus:ring-[#ffd700]/40";

  return (
    <div className="min-h-screen" style={{ background: '#0a1628', fontFamily: "'DM Sans', sans-serif" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800;900&display=swap');`}</style>
      {processing && <SpinnerOverlay message="Processing..." />}
      {showSuccess && <SuccessToast message={successMsg} visible={showSuccess} subMessage="" onDismiss={() => setShowSuccess(false)} />}
      {/* Quote Modal */}
      {quoteModal?.show && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.7)' }}>
          <div className="rounded-2xl p-6 w-full max-w-lg" style={{ background: '#0d1f3c', border: '1px solid rgba(255,255,255,0.1)' }}>
            <h3 className="text-white font-bold text-lg mb-4">Build Quote</h3>
            <div className="space-y-3 mb-5">
              {[
                { key: 'monthlyPrice', label: 'Monthly Lease Price (KES)', placeholder: '65000' },
                { key: 'depositTerms', label: 'Deposit Terms', placeholder: '2 months upfront' },
                { key: 'maintenanceFee', label: 'Maintenance Bundle Fee (KES/mo)', placeholder: '5000' },
                { key: 'overMileageRate', label: 'Over-Mileage Rate (KES/km)', placeholder: '25' },
                { key: 'slaTerms', label: 'SLA Terms', placeholder: '24hr vehicle replacement guarantee' },
              ]?.map(f => (
                <div key={f?.key}>
                  <label className="text-white/50 text-xs font-semibold uppercase tracking-wider mb-1.5 block">{f?.label}</label>
                  <input className={inputClass} style={inputStyle} placeholder={f?.placeholder} value={quoteForm?.[f?.key]} onChange={e => setQuoteForm(q => ({ ...q, [f?.key]: e?.target?.value }))} />
                </div>
              ))}
            </div>
            <div className="flex gap-3">
              <button onClick={() => setQuoteModal({ show: false, requestId: null })} className="btn-secondary flex-1">Cancel</button>
              <button onClick={handleSendQuote} className="btn-primary flex-1">Send Quote</button>
            </div>
          </div>
        </div>
      )}
      {/* Header */}
      <div className="sticky top-0 z-50 border-b border-white/10" style={{ background: 'rgba(10,22,40,0.95)', backdropFilter: 'blur(20px)' }}>
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center gap-3">
          <button onClick={() => navigate('/admin-dashboard')} className="p-2 rounded-xl hover:bg-white/10 transition-colors">
            <Icon name="ArrowLeft" size={20} className="text-white" />
          </button>
          <div>
            <h1 className="text-white font-bold text-lg">Corporate Contracts</h1>
            <p className="text-white/40 text-xs">Manage lease requests, contracts, and invoices</p>
          </div>
        </div>
      </div>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          {[
            { key: 'requests', label: 'Lease Requests', count: requests?.filter(r => r?.req_status === 'submitted')?.length },
            { key: 'leases', label: 'Active Contracts', count: leases?.filter(l => l?.lease_status === 'active')?.length },
            { key: 'invoices', label: 'Invoices', count: invoices?.filter(i => i?.inv_status === 'pending')?.length },
          ]?.map(tab => (
            <button key={tab?.key} onClick={() => setActiveTab(tab?.key)}
              className="px-5 py-2.5 rounded-xl text-sm font-semibold transition-all flex items-center gap-2"
              style={{ background: activeTab === tab?.key ? 'rgba(255,215,0,0.15)' : 'rgba(255,255,255,0.06)', color: activeTab === tab?.key ? '#ffd700' : 'rgba(255,255,255,0.5)', border: `1px solid ${activeTab === tab?.key ? 'rgba(255,215,0,0.3)' : 'rgba(255,255,255,0.1)'}` }}>
              {tab?.label}
              {tab?.count > 0 && <span className="px-1.5 py-0.5 rounded-full text-xs font-black" style={{ background: '#ffd700', color: '#0a1628' }}>{tab?.count}</span>}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="space-y-4">{[1, 2, 3]?.map(i => <div key={i} className="h-36 rounded-2xl animate-pulse" style={{ background: 'rgba(255,255,255,0.05)' }} />)}</div>
        ) : (
          <>
            {/* Requests Tab */}
            {activeTab === 'requests' && (
              <div className="space-y-4">
                {requests?.length === 0 ? <div className="text-center py-16"><div className="text-5xl mb-3">📋</div><p className="text-white/40 text-sm">No lease requests yet</p></div>
                  : requests?.map(req => {
                    const reqs = req?.requirements || {};
                    return (
                      <div key={req?.id} className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h3 className="text-white font-bold text-base">{req?.corporate_accounts?.company_name}</h3>
                            <p className="text-white/40 text-xs mt-0.5">{req?.corporate_accounts?.contact_person} · {req?.corporate_accounts?.billing_email}</p>
                          </div>
                          <span className="px-2 py-0.5 rounded-full text-xs font-bold capitalize" style={{ background: `${reqStatusColors?.[req?.req_status]}20`, color: reqStatusColors?.[req?.req_status] }}>{req?.req_status}</span>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs mb-4">
                          <div><div className="text-white/40">Vehicles</div><div className="text-white font-semibold">{reqs?.quantity} units</div></div>
                          <div><div className="text-white/40">Duration</div><div className="text-white font-semibold">{reqs?.durationMonths} months</div></div>
                          <div><div className="text-white/40">Types</div><div className="text-white font-semibold">{reqs?.vehicleTypes?.join(', ') || 'N/A'}</div></div>
                          <div><div className="text-white/40">Budget</div><div className="text-white font-semibold">{reqs?.budgetRange || 'N/A'}</div></div>
                        </div>
                        {req?.req_status === 'submitted' && (
                          <button onClick={() => setQuoteModal({ show: true, requestId: req?.id })} className="btn-primary text-sm py-2.5 px-5">
                            <Icon name="FileText" size={16} /> Build Quote
                          </button>
                        )}
                      </div>
                    );
                  })}
              </div>
            )}

            {/* Leases Tab */}
            {activeTab === 'leases' && (
              <div className="space-y-4">
                {leases?.length === 0 ? <div className="text-center py-16"><div className="text-5xl mb-3">📄</div><p className="text-white/40 text-sm">No contracts yet</p></div>
                  : leases?.map(lease => (
                    <div key={lease?.id} className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="text-white font-bold text-base">#{lease?.lease_number}</h3>
                          <p className="text-white/40 text-xs mt-0.5">{lease?.corporate_accounts?.company_name}</p>
                        </div>
                        <span className="px-2 py-0.5 rounded-full text-xs font-bold capitalize" style={{ background: `${leaseStatusColors?.[lease?.lease_status]}20`, color: leaseStatusColors?.[lease?.lease_status] }}>{lease?.lease_status}</span>
                      </div>
                      <div className="grid grid-cols-3 gap-3 text-xs mb-4">
                        <div><div className="text-white/40">Period</div><div className="text-white font-semibold">{formatDate(lease?.start_at)} → {formatDate(lease?.end_at)}</div></div>
                        <div><div className="text-white/40">Monthly</div><div className="text-[#ffd700] font-bold">KES {lease?.monthly_price?.toLocaleString()}</div></div>
                        <div><div className="text-white/40">Deposit Terms</div><div className="text-white font-semibold">{lease?.deposit_terms || 'N/A'}</div></div>
                      </div>
                      {lease?.lease_status === 'active' && (
                        <button onClick={() => handleCreateInvoice(lease?.id, lease?.corporate_id, lease?.monthly_price)} disabled={!!processing} className="btn-primary text-sm py-2 px-4">
                          <Icon name="Plus" size={14} /> Create Monthly Invoice
                        </button>
                      )}
                    </div>
                  ))}
              </div>
            )}

            {/* Invoices Tab */}
            {activeTab === 'invoices' && (
              <div className="space-y-3">
                {invoices?.length === 0 ? <div className="text-center py-16"><div className="text-5xl mb-3">💳</div><p className="text-white/40 text-sm">No invoices yet</p></div>
                  : invoices?.map(invoice => (
                    <div key={invoice?.id} className="rounded-2xl p-4 flex items-center justify-between" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                      <div>
                        <div className="text-white font-bold text-sm">#{invoice?.invoice_number}</div>
                        <div className="text-white/40 text-xs mt-0.5">{invoice?.corporate_accounts?.company_name} · Due: {formatDate(invoice?.due_date)}</div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-[#ffd700] font-black text-lg">KES {invoice?.amount?.toLocaleString()}</div>
                        <span className="px-2 py-0.5 rounded-full text-xs font-bold capitalize" style={{ background: `${invStatusColors?.[invoice?.inv_status]}20`, color: invStatusColors?.[invoice?.inv_status] }}>{invoice?.inv_status}</span>
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CorporateContractsScreen;
